/**
 * @prettier
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import _every from 'lodash/every';
import _fromPairs from 'lodash/fromPairs';
import { Banner } from '@zola/zola-ui/src/components/Banner';
import { Link } from '@zola/zola-ui/src/components/Link';
import { SETTINGS_BY_CARD_TYPE } from 'cards/constants/CardTypes';
import { getPricing } from 'cards/reducers/pricing/selectors';
import { fetchCardSuite as fetchCardSuiteAction } from 'cards/reducers/pdp/actions';
import { openModal as openModalAction } from 'cards/actions/modalActions';
import {
  getEnabledOptionsValuesList,
  enrichCardLevelOptions,
  enrichOptionsAccSelection,
} from 'cards/components/ProductDetail/AllOptionsSection/utils';
import FeatureFlags from '../../../../../util/featureFlags';
import BreadCrumbs from '../BreadCrumbs/BreadCrumbs';
import SamplePreview from '../SamplePreview/SamplePreview';
import Layout from '../Layout/Layout';
import { useDebounce } from '../util/hooks';
import {
  findVariation,
  lookupSelectedVariation,
  lookupDefaultVariation,
  getVariationOptions,
  getIsSampleCustomizable,
} from '../util/helpers';
import { STEPS } from '../constants/steps';
import styles from './styles.module.less';

export const CustomizationDropdown = {};

export const CustomizationBase = ({
  form,
  flowUUID,
  sampleNotCustomizable,
  isMagnet,
  isMulti,
  setMultiPreviewState,
  multiPreviewState,
  cardSuiteUUIDList,
  cardSuiteDetailsSet,
  goToStep,
  errors,
  step,
  breadcrumbSteps,
  colors,
  template,
  updateForm,
  openModal,
  closeModal,
  openPaperTypesModal,
  skuData,
  overrideActiveVariationUUID,
  cardSuiteDetails,
  isAdminView,
  calculatePricing,
  paperPricing,
}) => {
  const isPhotoValid = form.customizations.photo.valid;

  const hasFoil = suite =>
    !!suite.mediums.paper.available_options.foil.options.find(o => o.value !== 'none');

  const customizableFoilSuiteUUID =
    isMulti &&
    cardSuiteDetailsSet &&
    cardSuiteUUIDList.find(
      uuid =>
        getIsSampleCustomizable(cardSuiteDetailsSet[uuid]) && hasFoil(cardSuiteDetailsSet[uuid])
    );

  let customizableSuiteUUID =
    isMulti &&
    cardSuiteDetailsSet &&
    cardSuiteUUIDList.find(uuid => getIsSampleCustomizable(cardSuiteDetailsSet[uuid]));

  if (customizableFoilSuiteUUID) {
    customizableSuiteUUID = customizableFoilSuiteUUID;
  }

  const variation =
    form.customizations &&
    (isMulti && multiPreviewState.activePreview
      ? form.customizations.variationSet[customizableSuiteUUID]
      : form.customizations.variation);

  const customizableSuite = isMulti ? cardSuiteDetailsSet[customizableSuiteUUID] : cardSuiteDetails;

  const availableOptions = getVariationOptions(customizableSuite, isMagnet);

  const [selected, setSelected] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const uuid = selectedVariation && selectedVariation.uuid;
  const [pricingCalculated, setPricingCalculated] = useState(false);
  const defaultVariation = useMemo(() => lookupDefaultVariation(customizableSuite, isMagnet), [
    customizableSuite,
    isMagnet,
  ]);

  useEffect(() => {
    let overridedUUID =
      (variation && variation.uuid) || overrideActiveVariationUUID || defaultVariation.uuid;

    if (isMulti) {
      overridedUUID = (variation && variation.uuid) || defaultVariation.uuid;
    }

    if (!uuid && overridedUUID) {
      const data = findVariation(overridedUUID, customizableSuite, isMagnet);

      setSelectedVariation(data);
      setSelected(data.option_values);
    }
  }, [overrideActiveVariationUUID]);

  const getSelectedVariation = useCallback(
    (multiSuiteUUID = null) => {
      let suiteDetails = cardSuiteDetails;
      let color = form && form.customizations.color;

      const colorSet = form && form.customizations.colorSet;

      if (multiSuiteUUID) {
        color = colorSet[multiSuiteUUID];
        suiteDetails = cardSuiteDetailsSet[multiSuiteUUID];
      }

      if (uuid && selected) {
        const data = lookupSelectedVariation({ ...selected, color }, suiteDetails);

        return data;
      }

      return null;
    },
    [
      cardSuiteDetails,
      cardSuiteDetailsSet,
      uuid,
      form.customizations.color,
      form.customizations.colorSet,
      selected,
    ]
  );

  useDebounce(
    () => {
      if (isMulti) {
        const data = cardSuiteUUIDList
          .map(key => [key, getSelectedVariation(key)])
          .filter(d => !!d[1]);

        const variationSet = _fromPairs(data);

        const leadVariation = variationSet[customizableSuiteUUID];

        if (leadVariation) {
          setSelectedVariation(leadVariation);
          updateForm({ customizations: { variation: leadVariation, variationSet } });
        }
      } else {
        const data = getSelectedVariation();

        if (data) {
          setSelectedVariation(data);
          updateForm({ customizations: { variation: data } });
        }
      }
    },
    1000,
    [isMulti, uuid, selected, form.customizations.color, getSelectedVariation]
  );

  useEffect(() => {
    if (!pricingCalculated) {
      calculatePricing(customizableSuite.uuid, isAdminView).then(() => setPricingCalculated(true));
    }
  }, [customizableSuite.uuid, isAdminView]);

  const openPaperTypes = useCallback(() => {
    if (!pricingCalculated) {
      return;
    }

    const overrides = {
      cardType: customizableSuite.type,
      activeMediumKey: 'paper',
      activeSelection: selected,
      pricing: paperPricing,
    };

    closeModal();
    setTimeout(
      () =>
        openPaperTypesModal({ overrides }, () => {
          openModal({
            initialFlowUUID: flowUUID,
            initialForm: { ...form },
            initialStepKey: step.key,
          });
        }),
      50
    );
  }, [
    uuid,
    isAdminView,
    pricingCalculated,
    selected,
    customizableSuite,
    flowUUID,
    form,
    step.key,
    paperPricing,
  ]);

  const optionsWithFoil =
    FeatureFlags.get('invitesSingleSampleFoilDropdown') && hasFoil(customizableSuite)
      ? ['foil-color']
      : [];

  const sortedOptionsKeys = ['size', ...optionsWithFoil, 'paper-type', 'silhouette'];

  const dropdownProps = sortedOptionsKeys.reduce((allProps, sortedOptionsKey) => {
    if (!uuid || !selected || !availableOptions || !defaultVariation) {
      return null;
    }

    const uiData = availableOptions[sortedOptionsKey];

    if (!uiData) {
      return null;
    }

    const { key, options, label } = uiData;

    const renderedLabel =
      key === 'paper-type' ? (
        <div className={styles.paperTypeDropdownLabel}>
          <div className={styles.paperTypeDropdownLabelSpacing}>{label}</div>{' '}
          <Link role="button" onClick={openPaperTypes} arrow>
            Compare Our Paper Types
          </Link>
        </div>
      ) : (
        label
      );

    const enabledOptionsValuesList = getEnabledOptionsValuesList(options);
    const cardTypeOptions = enrichCardLevelOptions(
      key,
      options,
      customizableSuite.type,
      enabledOptionsValuesList,
      isMagnet ? 'magnet' : 'paper',
      isAdminView
    );
    let enrichedOptions = enrichOptionsAccSelection(
      key,
      cardTypeOptions,
      selected,
      customizableSuite.type,
      paperPricing,
      isMagnet ? 'magnet' : 'paper'
    );

    const onSelect = value => {
      let withFoil = {};
      if (key === 'foil-color') {
        withFoil = {
          foil: value === 'none' ? 'none' : 'custom',
        };
      }

      if (key === 'size') {
        withFoil = {
          foil: value === 'postcard' ? 'none' : selected.foil,
          'foil-color': value === 'postcard' ? 'none' : selected['foil-color'],
        };
      }

      setSelected({ ...selected, ...withFoil, [key]: value });
    };

    // Hide postcard option for letterpress cards
    if (key === 'size' && cardSuiteDetails.letterpress) {
      enrichedOptions = enrichedOptions.filter(option => option.value !== 'postcard');
    }

    if (key === 'foil-color' && selected.size === 'postcard') {
      enrichedOptions = enrichedOptions.filter(option => option.value === 'none');
    }

    return {
      ...allProps,
      [sortedOptionsKey]: {
        key,
        name: key,
        label: renderedLabel,
        options: enrichedOptions.filter(option => !option.disabled),
        selected: selected[key],
        onSelect,
      },
    };
  }, {});

  // Reset unsupported selected options if dropdown options get disabled changes
  useEffect(() => {
    if (!dropdownProps) {
      return;
    }

    const updatedSelected = Object.keys(dropdownProps).reduce((newSelected, dropdownPropKey) => {
      const dropdownMatch = dropdownProps[dropdownPropKey].options.find(
        option => option.value === selected[dropdownPropKey]
      );

      return {
        ...selected,
        ...newSelected,
        [dropdownPropKey]: dropdownMatch
          ? dropdownMatch.value
          : selected[dropdownProps] || defaultVariation.option_values[dropdownPropKey],
      };
    }, {});

    if (updatedSelected.size === 'postcard') {
      updatedSelected['reverse-printing'] = 'true';
    }

    if (_every(Object.keys(selected).map(k => selected[k] === updatedSelected[k]))) {
      return;
    }

    setSelected(updatedSelected);
  }, [selected, defaultVariation]);

  if (!uuid || !selected || !availableOptions || !defaultVariation) {
    return null;
  }

  const cardUrlSlug = SETTINGS_BY_CARD_TYPE[customizableSuite.type].urlSlug;

  let nextDisabled = !(
    variation && _every(sortedOptionsKeys.map(k => variation.option_values[k] === selected[k]))
  );

  if (sampleNotCustomizable) {
    nextDisabled = false;
  }

  return (
    <Layout.Form onSubmit={() => goToStep(STEPS.WEDDING_DETAILS_FORM)}>
      <Layout.Box>
        <Layout.Row>
          <Layout.PreviewBox>
            <SamplePreview
              hideColorPicker={sampleNotCustomizable}
              colors={colors}
              template={template}
              form={form}
              updateForm={updateForm}
              sampleNotCustomizable={sampleNotCustomizable}
              skuData={skuData}
              cardSuiteDetails={cardSuiteDetails}
              isMagnet={isMagnet}
              isMulti={isMulti}
              setMultiPreviewState={setMultiPreviewState}
              multiPreviewState={multiPreviewState}
              cardSuiteUUIDList={cardSuiteUUIDList}
              cardSuiteDetailsSet={cardSuiteDetailsSet}
            />
          </Layout.PreviewBox>
          <Layout.Col>
            <BreadCrumbs.Text step={step} breadcrumbSteps={breadcrumbSteps} />
            <Layout.Header>Customize Your Sample</Layout.Header>
            <Layout.Text>
              Make it feel like you in just a few clicks. Want to customize more? See all our
              options{' '}
              <Link
                href={`/wedding-planning/${cardUrlSlug}/${cardSuiteDetails.uuid}`}
                target="_blank"
                className={styles.paperTypesModalLink}
              >
                here
              </Link>
              , and order a proof at checkout instead.
            </Layout.Text>
            {errors && errors.variation && <Banner type="error">{errors.variation}</Banner>}
            {sortedOptionsKeys.map(
              key =>
                dropdownProps[key] &&
                dropdownProps[key].options.length > 0 && (
                  <Layout.Dropdown key={key} {...dropdownProps[key]} />
                )
            )}
            <Layout.FormButtons
              backLabel="Back"
              nextLabel="Next"
              backDisabled={breadcrumbSteps.findIndex(k => k === step.key) === 0}
              nextDisabled={nextDisabled}
              onBack={() => {
                if (isMulti) {
                  goToStep(STEPS.MULTI_UPLOAD_HELP);
                } else {
                  goToStep(isPhotoValid ? STEPS.PHOTO_CROP : STEPS.FILE_UPLOAD);
                }
              }}
            />
          </Layout.Col>
        </Layout.Row>
        <BreadCrumbs.Dots step={step} breadcrumbSteps={breadcrumbSteps} />
      </Layout.Box>
    </Layout.Form>
  );
};

export const mapStateToProps = state => ({
  paperPricing: getPricing(state),
});

export const mapDispatchToProps = dispatch => ({
  // This is only used to populate pricing info in redux
  calculatePricing: (uuid, isAdminView) => dispatch(fetchCardSuiteAction(uuid, isAdminView)),
  openPaperTypesModal: (modalProps, closeCallback) =>
    dispatch(
      openModalAction('PAPER_TYPE', modalProps, {
        closeCallback,
        preventClose: true,
      })
    ),
});

export default connect(null, mapDispatchToProps)(CustomizationBase);
